import { Reducer } from "redux";

import { getLocale } from "@utils/configuration/aem";
import { attemptMethod } from "@utils/general";
import { parseElementForCommentedContent } from "@utils/ssr";
import { flattenObj } from "@utils/translations";

import { VALUE_STAGES } from "./create-value-module";
import { rebuildUrls } from "./modules/value/flyoutCategories/helpers/flyoutCategories.helpers";
import {
    $ValueModuleName,
    IState,
    IValueState,
    TStore,
    TValueAction,
} from "./store.types";
import { formatSingleContants } from "./utils";

type TComponentDataInfo = {
    key?: TValueStateKey;
    state?: Object;
};

const DataMappers: Partial<{
    [K in TValueStateKey]: (data: any) => IState["value"][K];
}> = {
    flyoutCategories: data => rebuildUrls(data, `/${getLocale(true)}/`),
    translations: data => ({ data: flattenObj(data.data), debug: data.debug }),
};

type TValueStateKey = keyof IState["value"];

const parseComponent = (component: HTMLElement): TComponentDataInfo => {
    return attemptMethod<TComponentDataInfo>(() => {
        const key = component.dataset.ssrInitialdata as
            | TValueStateKey
            | undefined;
        const json = parseElementForCommentedContent(component);
        return {
            key,
            state: json,
        };
    }, {});
};

const resolveInitialState = (store: TStore) => {
    if (typeof window !== "undefined" && typeof document !== "undefined") {
        const elementNodeListOf: NodeList = document.querySelectorAll(
            "script[data-ssr-initialdata]",
        );
        const nodes: Node[] = [...elementNodeListOf];
        nodes.forEach(node => {
            const { key, state } = parseComponent(node as HTMLElement);
            if (!!key && state) {
                const type = formatSingleContants(
                    $ValueModuleName,
                    key,
                    VALUE_STAGES.Init,
                );

                store.dispatch({
                    type,
                    payload: {
                        ...(DataMappers[key]
                            ? DataMappers[key]?.(state)
                            : state),
                    },
                });
            }
        });
    }
};

const initializingReducer =
    <S>(key: keyof IValueState): Reducer<S | undefined, TValueAction> =>
    (state: any = {}, action: TValueAction) => {
        const { type, payload } = action;
        const matches = /(.*)\/(.*)\/(init)/.exec(type);

        if (!matches) return state;

        const [, prefix, namespace, valueStage] = matches;
        if (prefix !== $ValueModuleName || key !== namespace) {
            return state;
        }

        if (valueStage.toUpperCase() === VALUE_STAGES.Init) {
            return { ...state, ...payload };
        }

        return state;
    };

export { resolveInitialState, initializingReducer };
